import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getNumberInputValue } from '../utils/numberInputHelpers';
import { useSystemFeatures } from '../hooks/useSystemFeatures';
import TableAreasManagement from './TableAreasManagement';

function TablesManagement({
    tables,
    theme,
    showDeletedTables,
    setShowDeletedTables,
    showAddTable,
    setShowAddTable,
    handleOpenEditTable,
    handleDeleteTable,
    floorPlanSettings = {},
    onSaveFloorPlanSettings,
    areas = [],
    onAreasChanged
}) {
    const { t } = useLanguage();
    const { tableAreasEnabled } = useSystemFeatures();
    const [searchTerm, setSearchTerm] = useState('');

    // Local state for floor plan inputs — allows free typing without clamping
    const [localFloorPlan, setLocalFloorPlan] = useState({
        floorPlanWidth: floorPlanSettings.floorPlanWidth ?? 600,
        floorPlanHeight: floorPlanSettings.floorPlanHeight ?? 400,
        gridSnapSize: floorPlanSettings.gridSnapSize ?? 0,
    });
    const saveTimerRef = useRef(null);

    // Sync from parent when props change (e.g. initial fetch)
    useEffect(() => {
        setLocalFloorPlan({
            floorPlanWidth: floorPlanSettings.floorPlanWidth ?? 600,
            floorPlanHeight: floorPlanSettings.floorPlanHeight ?? 400,
            gridSnapSize: floorPlanSettings.gridSnapSize ?? 0,
        });
    }, [floorPlanSettings.floorPlanWidth, floorPlanSettings.floorPlanHeight, floorPlanSettings.gridSnapSize]);

    // Calculate minimum canvas size from active table positions
    const activeTables = useMemo(() => tables.filter(tb => tb.deleted === 0 && tb.temporary === 0), [tables]);
    const minCanvas = useMemo(() => {
        let minW = 200, minH = 200;
        activeTables.forEach(table => {
            const right = (table.x || 0) + (table.width || 40) + 10;
            const bottom = (table.y || 0) + (table.height || 40) + 10;
            if (right > minW) minW = right;
            if (bottom > minH) minH = bottom;
        });
        return { width: Math.ceil(minW), height: Math.ceil(minH) };
    }, [activeTables]);

    // Debounced save — waits 800ms after last keystroke, only saves valid numbers
    const debouncedSave = useCallback((key, rawValue, min, max) => {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => {
            const num = parseInt(rawValue, 10);
            if (!isNaN(num) && onSaveFloorPlanSettings) {
                const clamped = Math.max(min, Math.min(max, num));
                // Check if value is smaller than what tables need
                if (key === 'floorPlanWidth' && clamped < minCanvas.width) {
                    alert(`${t('floorPlanTooSmall')} ${minCanvas.width} x ${minCanvas.height}.\n\n${t('floorPlanTooSmallHint')}.`);
                    setLocalFloorPlan(prev => ({ ...prev, [key]: floorPlanSettings[key] ?? 600 }));
                    return;
                }
                if (key === 'floorPlanHeight' && clamped < minCanvas.height) {
                    alert(`${t('floorPlanTooSmall')} ${minCanvas.width} x ${minCanvas.height}.\n\n${t('floorPlanTooSmallHint')}.`);
                    setLocalFloorPlan(prev => ({ ...prev, [key]: floorPlanSettings[key] ?? 400 }));
                    return;
                }
                setLocalFloorPlan(prev => ({ ...prev, [key]: clamped }));
                onSaveFloorPlanSettings({ [key]: clamped });
            }
        }, 800);
    }, [onSaveFloorPlanSettings, minCanvas, t, floorPlanSettings]);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => clearTimeout(saveTimerRef.current);
    }, []);

    const handleFloorPlanInput = (key, value, min, max) => {
        setLocalFloorPlan(prev => ({ ...prev, [key]: value }));
        debouncedSave(key, value, min, max);
    };

    // Filter tables based on search term
    const matchesSearch = (table) => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return [
            table.name,
            table.shape,
            table.size,
            table.width?.toString(),
            table.height?.toString(),
            table.deleted ? t('deleted') : t('active')
        ].some(field =>
            field && field.toString().toLowerCase().includes(search)
        );
    };

    const filteredTables = tables.filter(matchesSearch);
    return (
        <div style={{
            background: theme.colors.surface,
            padding: '0rem',
            borderRadius: theme.borderRadius || 8,
            boxShadow: theme.boxShadow
        }}>
            <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <button
                        onClick={() => setShowAddTable(true)}
                        style={{
                            marginTop: '0.5rem',
                            padding: '0.5rem 1rem',
                            backgroundColor: theme.colors.primary,
                            color: theme.colors.surface,
                            border: 'none',
                            borderRadius: theme.borderRadius || 4,
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: 'bold'
                        }}
                    >
                        + {t('addTable')}
                    </button>
                </div>

                {/* Search Input */}
                <div style={{ marginBottom: '1rem' }}>
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={t('searchTables')}
                        style={{
                            width: '100%',
                            padding: '0.75rem',
                            fontSize: '0.95rem',
                            border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                            borderRadius: theme?.borderRadius || 4,
                            backgroundColor: theme?.colors?.surface || '#fff',
                            color: theme?.colors?.text || '#000',
                            outline: 'none'
                        }}
                        onFocus={(e) => {
                            e.target.style.borderColor = theme?.colors?.primary || '#1976d2';
                        }}
                        onBlur={(e) => {
                            e.target.style.borderColor = theme?.colors?.border || '#ddd';
                        }}
                    />
                    {searchTerm && (
                        <div style={{
                            marginTop: '0.5rem',
                            fontSize: '0.85rem',
                            color: theme?.colors?.textSecondary || '#666'
                        }}>
                            {t('showing')} {filteredTables.length} {t('of')} {tables.length} {t('tables').toLowerCase()}
                        </div>
                    )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <label style={{ fontSize: '0.9rem', color: theme.colors.text }}>
                        <input
                            type="checkbox"
                            checked={showDeletedTables}
                            onChange={(e) => setShowDeletedTables(e.target.checked)}
                            style={{ marginRight: '0.5rem' }}
                        />
                        {t('showDeletedTables')}
                    </label>
                </div>
            </div>
            {/* Table Areas - only where the tenant has the feature on */}
            {tableAreasEnabled && (
                <TableAreasManagement theme={theme} onAreasChanged={onAreasChanged} />
            )}

            {/* Floor Plan Layout Settings */}
            {onSaveFloorPlanSettings && (
                <div style={{
                    marginBottom: '0.75rem',
                    padding: '0.5rem 0.75rem',
                    background: theme?.colors?.background || '#f5f5f5',
                    borderRadius: theme?.borderRadius || 8,
                    border: `1px solid ${theme?.colors?.border || '#ddd'}`
                }}>
                    <div style={{
                        display: 'flex',
                        gap: '0.5rem',
                        alignItems: 'center',
                        flexWrap: 'wrap'
                    }}>
                        <span style={{
                            fontWeight: 'bold',
                            color: theme?.colors?.primary || '#1976d2',
                            fontSize: '0.85rem',
                            marginRight: '0.25rem',
                            whiteSpace: 'nowrap'
                        }}>
                            {t('floorPlanLayout')}:
                        </span>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.8rem', color: theme?.colors?.text || '#000' }}>
                            W
                            <input
                                type="number"
                                value={getNumberInputValue(localFloorPlan.floorPlanWidth)}
                                onChange={(e) => handleFloorPlanInput('floorPlanWidth', e.target.value, 200, 2000)}
                                onWheel={(e) => e.target.blur()}
                                placeholder="600"
                                style={{
                                    width: '70px',
                                    padding: '0.3rem 0.4rem',
                                    border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                                    borderRadius: theme?.borderRadius || 4,
                                    fontSize: '0.85rem',
                                    backgroundColor: theme?.colors?.surface || '#fff',
                                    color: theme?.colors?.text || '#000'
                                }}
                            />
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.8rem', color: theme?.colors?.text || '#000' }}>
                            H
                            <input
                                type="number"
                                value={getNumberInputValue(localFloorPlan.floorPlanHeight)}
                                onChange={(e) => handleFloorPlanInput('floorPlanHeight', e.target.value, 200, 2000)}
                                onWheel={(e) => e.target.blur()}
                                placeholder="400"
                                style={{
                                    width: '70px',
                                    padding: '0.3rem 0.4rem',
                                    border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                                    borderRadius: theme?.borderRadius || 4,
                                    fontSize: '0.85rem',
                                    backgroundColor: theme?.colors?.surface || '#fff',
                                    color: theme?.colors?.text || '#000'
                                }}
                            />
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.8rem', color: theme?.colors?.text || '#000' }}>
                            {t('gridSnapSize')}
                            <input
                                type="number"
                                value={getNumberInputValue(localFloorPlan.gridSnapSize)}
                                onChange={(e) => handleFloorPlanInput('gridSnapSize', e.target.value, 0, 100)}
                                onWheel={(e) => e.target.blur()}
                                placeholder="0"
                                style={{
                                    width: '55px',
                                    padding: '0.3rem 0.4rem',
                                    border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                                    borderRadius: theme?.borderRadius || 4,
                                    fontSize: '0.85rem',
                                    backgroundColor: theme?.colors?.surface || '#fff',
                                    color: theme?.colors?.text || '#000'
                                }}
                            />
                            px
                        </label>
                    </div>
                </div>
            )}

            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {filteredTables.map(table => (
                    <div key={table.id} style={{
                        padding: '0.75rem',
                        marginBottom: '0.5rem',
                        background: theme.colors.background,
                        borderRadius: theme.borderRadius || 6,
                        border: `1px solid ${theme.colors.primary}20`,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <div>
                            <strong>{table.name}</strong>
                            {table.active_orders > 0 && (
                                <span style={{
                                    marginLeft: '0.5rem',
                                    backgroundColor: '#ff9800',
                                    color: 'white',
                                    padding: '0.1rem 0.4rem',
                                    borderRadius: '10px',
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold'
                                }}>
                                    {table.active_orders} {table.active_orders > 1 ? t('orders') : t('order')}
                                </span>
                            )}
                            <div style={{ fontSize: '0.8rem', color: theme.colors.textSecondary }}>
                                {t('shape')}: {table.shape} | {t('size')}: {table.width}x{table.height}
                                {tableAreasEnabled && areas.length > 0 && (
                                    <> | {t('area')}: {(areas.find(a => a.id === (table.area_id || 1)) || {}).name || ''}</>
                                )}
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <div style={{
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                background: table.deleted ? '#ffebee' : '#e8f5e8',
                                color: table.deleted ? '#c62828' : '#2e7d32',
                                fontSize: '0.8rem',
                                fontWeight: 'bold'
                            }}>
                                {table.deleted ? t('deleted') : t('active')}
                            </div>
                            <button
                                onClick={() => handleOpenEditTable(table)}
                                style={{
                                    padding: '0.25rem 0.5rem',
                                    backgroundColor: theme.colors.secondary || '#666',
                                    color: theme.colors.surface,
                                    border: 'none',
                                    borderRadius: theme.borderRadius || 4,
                                    cursor: 'pointer',
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold'
                                }}
                                title={t('editTable')}
                            >
                                ✏️
                            </button>
                            <button
                                onClick={() => handleDeleteTable(table.id)}
                                style={{
                                    padding: '0.25rem 0.5rem',
                                    backgroundColor: '#d32f2f',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: theme.borderRadius || 4,
                                    cursor: 'pointer',
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold',
                                    marginLeft: '0.5rem'
                                }}
                                title={t('deleteTable')}
                            >
                                🗑️
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default TablesManagement;